// src/pages/Signup.tsx

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const isAllowedRedirect = (url: string): boolean => {
  if (url.startsWith('/')) {
    return true;
  }

  try {
    const parsed = new URL(url);
    const { hostname } = parsed;
    return (
      hostname === 'snowbirdindustries.us' ||
      hostname.endsWith('.snowbirdindustries.us') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1'
    );
  } catch (error) {
    return false;
  }
};

const withToken = (url: string, token?: string | null): string => {
  if (!token) {
    return url;
  }
  const parsed = new URL(url);
  parsed.searchParams.set('token', token);
  return parsed.toString();
};

const Signup: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();

  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const redirectTarget = redirectParam && isAllowedRedirect(redirectParam) ? redirectParam : null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await signup(name, email, password);
      if (success) {
        if (redirectTarget) {
          const token = localStorage.getItem('authToken');
          const targetUrl = redirectTarget.startsWith('/')
            ? redirectTarget
            : withToken(redirectTarget, token);
          if (targetUrl.startsWith('/')) {
            navigate(targetUrl, { replace: true });
          } else {
            window.location.assign(targetUrl);
          }
          return;
        }
        navigate('/profile');
      } else {
        setError('Unable to create account. Please check your details.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="section container">
      <div style={{
        maxWidth: '500px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Create Account</h1>
        <p className="text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Create your Snowbird Industries account to access games and apps.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              color: '#dc2626',
            }}>
              {error}
            </div>
          )}
          <div>
            <label
              htmlFor="name"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}
            >
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Your name"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Create a password"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '0.75rem',
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Creating...' : 'Create Account'}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          Already have an account?{' '}
          <a href={redirectTarget ? `/login?redirect=${encodeURIComponent(redirectTarget)}` : '/login'}>
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
};

export default Signup;
